/**
 * This is a modified version of solr-query which is limited to run in NodeJS, this is only the query builder part
 * to make it compatible to run in the browser.
 * See: https://github.com/lbdremy/solr-node-client
 */
import queryStringify from "./utils/queryStringify"
import * as format from "./utils/format"
import * as arrayUtils from "./utils/array"
import * as versionUtils from "./utils/version"
import {
  Filters,
  HlOptions,
  MltOptions,
  TermsOptions,
  GroupOptions,
  FacetOptions,
  DateOptions,
  JoinOptions,
  MatchFilterOptions,
} from "./types"
import { dateISOify } from "./utils/format"

const COMPLEX_PHRASE_PARAM = "{!complexphrase inOrder=true}"

export type SolrQueryOptions = {
  solrVersion?: number
  isGlobalIndex?: boolean
}

/**
 * Create a new `Query`
 * @constructor
 *
 * @return {SolrQueryBuilder}
 * @api private
 */
export class SolrQueryBuilder {
  public solrVersion: number | undefined
  public parameters: any[]
  public customQueryParameters: string
  public isGlobalIndex: boolean

  constructor(options?: SolrQueryOptions) {
    this.solrVersion = (options && options.solrVersion) || undefined
    this.parameters = []
    this.customQueryParameters = ""
    this.isGlobalIndex = (options && options.isGlobalIndex) || false
  }

  /**
   * Set a new parameter
   * Since all possibilities provided by Solr are not available in the `Query` object, `set()` is there to fit this gap.
   *
   * @param {String} parameter - string, special characters have to be correctly encoded or the request will fail.
   *
   * @return {SolrQueryBuilder} - allow chaining
   * @api public
   */
  set(parameter: string): SolrQueryBuilder {
    const self = this
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the query parser to use with this request.
   *
   * @param {String} type - name of the query parser e.g: 'dismax'
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  defType(type: string): SolrQueryBuilder {
    const self = this
    const parameter = "defType=" + type
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the Request Handler used to process the request based on its `name`.
   * Works only if no Request Handler has been configured with `/select` as its name in solrconfig.xml.
   *
   * @param {String} name - name of the Request Handler
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  requestHandler(name: string): SolrQueryBuilder {
    const self = this
    const parameter = "qt=" + name
    this.parameters.push(parameter)
    return self
  }

  qt = SolrQueryBuilder.prototype.requestHandler

  customq(queryParameters: string[]): SolrQueryBuilder {
    const self = this
    this.customQueryParameters = queryParameters.join("%20AND%20")
    return self
  }

  /**
   *  Set the main query
   *
   * @param {String|Object} q -
   *
   * @param matchFilterOptions
   * @return  {SolrQueryBuilder}
   * @api public
   */
  q(q: string | Record<string, any>, matchFilterOptions?: MatchFilterOptions): SolrQueryBuilder {
    const self = this
    const separator = this.isGlobalIndex ? "%20OR%20" : "%20AND%20"

    const parameter =
      typeof q === "string"
        ? encodeURIComponent(q)
        : queryStringify(q, separator, ":", {}, this.isGlobalIndex)

    const fullParameter = matchFilterOptions?.complexPhrase
      ? encodeURIComponent(COMPLEX_PHRASE_PARAM) + parameter
      : parameter

    this.parameters.push(
      "q=" +
        fullParameter +
        (this.customQueryParameters ? "%20AND%20" + this.customQueryParameters : "")
    )

    return self
  }

  /**
   *  Set the default query operator
   *
   * @param {String} op -
   *
   * @return  {SolrQueryBuilder}
   * @api public
   */

  qop(op: string): SolrQueryBuilder {
    const self = this
    let parameter = "q.op="
    parameter += op
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the default query field.
   *
   * @param {String} df - the default field where solr should search.
   *
   * @return  {SolrQueryBuilder}
   * @api public
   */
  df(df: string): SolrQueryBuilder {
    const self = this
    let parameter = "df="
    parameter += df
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the offset where the set of returned documents should begin.
   *
   * @param {Number} start - the offset where the set of returned documents should begin.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  start(start: number): SolrQueryBuilder {
    const self = this
    const parameter = "start=" + start
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the maximum number of documents returned
   *
   * @param {Number} rows - number of documents
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  rows(rows: number): SolrQueryBuilder {
    const self = this
    const parameter = "rows=" + rows
    this.parameters.push(parameter)
    return self
  }

  /**
   * Request to use cursorMarks for deep-paging as explained in http://heliosearch.org/solr/paging-and-deep-paging/
   * Note that usage of a cursor requires a sort containing a uniqueKey field tie breaker
   *
   * @param mark
   *   The mark to use.
   *   Defaults to "*" to request a new cursor in the first request.
   */
  cursorMark(mark = "*"): SolrQueryBuilder {
    const self = this
    const parameter = "cursorMark=" + encodeURIComponent(mark)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Sort a result in descending or ascending order based on one or more fields.
   *
   * @param {Object} options -
   * @param {string} options -
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  sort(options: Record<string, any>, extra = ""): SolrQueryBuilder {
    const self = this
    let parameter = "sort="
    parameter += queryStringify(options, ",", "%20", {}, this.isGlobalIndex)
    if (extra) {
      parameter += "," + encodeURIComponent(extra)
    }

    this.parameters.push(parameter)
    return self
  }

  /**
   * Filter the set of documents found before to return the result with the given range determined by `field`, `start` and `end`.
   *
   * @param {Array|Object} options -
   * @param {String} options2.field - the name of the field where the range is applied
   * @param {String|Number|Date} options2.start - the offset where the range starts
   * @param {String|Number|Date} options2.end - the offset where the range ends
   *
   * @return {SolrQueryBuilder}
   * @api public
   *
   * @example
   * var query = client.query();
   * query.q({ '*' : '*' }).rangeFilter({ field : 'id', start : 100, end : 200})
   * // also works
   * query.q({ '*' : '*' }).rangeFilter([{ field : 'id', start : 100, end : 200},{ field : 'date', start : new Date(), end : new Date() - 3600}]);
   */

  rangeFilter(options: DateOptions | DateOptions[]): SolrQueryBuilder {
    const self = this
    const options2 = format.dateISOify(options)
    let parameter = "fq="
    if (Array.isArray(options2)) {
      parameter += "("
      const filters = options2.map(function (option) {
        const key = option.field
        const startParam = option.start ? option.start.toString() : "*"
        const endParam = option.end ? option.end.toString() : "*"
        const filter: Record<string, string> = {}
        filter[key] = "[" + startParam + "%20TO%20" + endParam + "]"
        return format.stringify(filter, "", ":")
      })
      parameter += filters.join("%20AND%20")
      parameter += ")"
    } else {
      const key = options2.field
      const startParam = options2.start ? options2.start.toString() : "*"
      const endParam = options2.end ? options2.end.toString() : "*"
      const filter: Record<string, string> = {}
      filter[key] = "[" + startParam + "%20TO%20" + endParam + "]"
      parameter += format.stringify(filter, "", ":")
    }
    this.parameters.push(parameter)
    return self
  }
  /**
   * Filter the set of documents found before to return the result by  joining inner data from one solr connection (core) to another (core).
   *
   * @return {SolrQueryBuilder}
   * @api public
   *
   * @example
   * var query = client.query();
   * query.q({ '*' : '*' }).joinFilter{fromIndex='organizations', from='region_s', to='region_s', v='mgr_s:yes'}}
   */
  joinFilter(options: JoinOptions): SolrQueryBuilder {
    const self = this
    let parameter = "fq="
    const filter = `{!join fromIndex=${options.fromIndex} from=${options.from} to=${options.to} v='${options.field}:${options.value}'}`
    parameter += encodeURIComponent(filter)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Filter the set of documents found before to return the result with the given `field` and `value`.
   *
   * @param {String} field - name of field
   * @param {String|String[]|Number|Number[]|Date|Date[]} value - value of the field that must match
   *
   * @param matchFilterOptions
   * @return {SolrQueryBuilder}
   * @api public
   *
   * @example
   * var query = client.query();
   * query.q({ '*' : '*' }).matchFilter('id', 100)
   * query.q({ '*' : '*' }).matchFilter('id', [100, 200])
   */

  matchFilter(
    field: string,
    value: string | string[] | number | number[] | Date | Date[] | boolean,
    matchFilterOptions?: MatchFilterOptions
  ): SolrQueryBuilder {
    const self = this
    let parameter = "fq="
    if (Array.isArray(value)) {
      value = value.map(dateISOify)
      value = `(${value.join(" OR ")})`
    } else {
      value = format.dateISOify(value)
      field = matchFilterOptions?.complexPhrase ? `{!complexphrase inOrder=true}${field}` : field
    }
    parameter += encodeURIComponent(field + ":" + value)
    this.parameters.push(parameter)
    return self
  }

  /**
   * wrapper function for matchFilter, accepting either an object with `field` and `value` properties
   * or an array containing such objects to be mapped on matchFilter
   *
   * @param {Object|Array} filters - Object or an array of objects with `field` and `value` properties
   *
   * @return {SolrQueryBuilder}
   *
   * @throws {Error}
   * @api public
   *
   * @example
   * var query = client.query();
   * query.q({ '*' : '*' }).fq({field: 'id', value: 100})
   * query.q({ '*' : '*' }).fq([{field: 'id', value: 100}, {field: 'name', value: 'John'}])
   */
  fq(filters: Filters | Filters[]): SolrQueryBuilder {
    const self = this
    if (Array.isArray(filters)) {
      filters.map((f) => {
        if (f.value === undefined || f.value === "") {
          let parameter = "fq=" + encodeURIComponent(f.field)
          this.parameters.push(parameter)
        } else {
          this.matchFilter(f.field, f.value, f.matchFilterOptions)
        }
      })
      return self
    }
    if (filters instanceof Object) {
      if (filters.value === undefined || filters.value === "") {
        let parameter = "fq=" + encodeURIComponent(filters.field)
        this.parameters.push(parameter)
        return self
      }
      return this.matchFilter(filters.field, filters.value, filters.matchFilterOptions)
    } else {
      throw new Error("unknown type for filter in fq()")
    }
  }

  /**
   * Specify a set of fields to return.
   *
   * @param {String|Array} fields - field name
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  fl(fields: string | string[]): SolrQueryBuilder {
    const self = this
    let parameter = "fl="
    if (typeof fields === "string") {
      parameter += encodeURIComponent(fields)
    } else {
      parameter += encodeURIComponent(fields.join(","))
    }
    this.parameters.push(parameter)
    return self
  }

  restrict = SolrQueryBuilder.prototype.fl

  /**
   * Set the time allowed for a search to finish.
   * Partial results may be returned (if there are any).
   *
   * @param {String|Number} time - time is in milliseconds. Values <= 0 mean no time restriction.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  timeout(time: string | number): SolrQueryBuilder {
    const self = this
    const parameter = "timeAllowed=" + time
    this.parameters.push(parameter)
    return self
  }

  /**
   * Group documents with the given `field`
   *
   * @param {String} field - field name
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  groupBy(field: string): SolrQueryBuilder {
    const self = this
    this.group({
      field: field,
    })
    return self
  }

  /**
   * Group documents using field collapsing or result grouping feature.
   * Field Collapsing collapses a group of results with the same field value down to a single (or fixed number) of entries.
   * Result Grouping groups documents with a common field value into groups, returning the top documents per group, and the top groups based on what documents are in the groups.
   *
   * @param {Object} options
   * @param {Boolean} [options.on=true] - if false, turn off result grouping, otherwise turn on.
   * @param {String|Array} options.field - Group based on the unique values of a field.
   * @param {String|Array} options.query - Return a single group of documents that also match the given query.
   * @param {Number} [options.limit=1] - The number of results (documents) to return for each group. Solr's default value is 1.
   * @param {Number} options.offset - The offset into the document list of each group.
   * @param {String} [options.sort="score desc"] - How to sort documents within a single group. Defaults to the same value as the sort parameter.
   * @param {String} options.format - if simple, the grouped documents are presented in a single flat list. The start and rows parameters refer to numbers of documents instead of numbers of groups.
   * @param {Boolean} options.main - If true, the result of the last field grouping command is used as the main result list in the response, using group.format=simple.
   * @param {Boolean} [options.ngroups=false] - If true, includes the number of groups that have matched the query. Default is false.
   * @param {Boolean} options.truncate - If true, facet counts are based on the most relevant document of each group matching the query. Same applies for StatsComponent. Default is false.
   * @param {Number}  [options.cache=0] - If > 0 enables grouping cache. Grouping is executed actual two searches. This option caches the second search. A value of 0 disables grouping caching. Default is 0.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  group(options: GroupOptions): SolrQueryBuilder {
    const self = this
    if (options.on === false) {
      this.parameters.push("group=false")
    } else {
      this.parameters.push("group=true")
    }
    if (options.field) {
      options.field = arrayUtils.toArray(options.field)
      options.field.forEach(function (field) {
        self.parameters.push("group.field=" + field)
      })
    }
    if (options.query) {
      if (!Array.isArray(options.query)) {
        options.query = [options.query]
      }
      options.query.forEach(function (query: string) {
        self.parameters.push("group.query=" + encodeURIComponent(query))
      })
    }
    if (options.limit !== undefined) {
      this.parameters.push("group.limit=" + options.limit)
    }
    if (options.offset !== undefined) {
      this.parameters.push("group.offset=" + options.offset)
    }
    if (options.sort) {
      this.parameters.push("group.sort=" + encodeURIComponent(options.sort))
    }
    if (options.format) {
      this.parameters.push("group.format=" + encodeURIComponent(options.format))
    }
    if (options.main !== undefined) {
      this.parameters.push("group.main=" + options.main)
    }
    if (options.ngroups !== undefined) {
      this.parameters.push("group.ngroups=" + options.ngroups)
    }
    if (options.truncate !== undefined) {
      this.parameters.push("group.truncate=" + options.truncate)
    }
    if (options.cache !== undefined) {
      this.parameters.push("group.cache.percent=" + options.cache)
    }
    return self
  }

  /**
   * Create a facet
   *
   * @param {Object} options - set of options to create a facet
   * @param {Boolean} [options.on=true] - Turn on or off facet
   * @param {String} [options.query] - This parameter allows you to specify an arbitrary query in the Lucene default syntax to generate a facet count. By default, faceting returns a count of the unique terms for a "field", while facet.query allows you to determine counts for arbitrary terms or expressions.
   * @param {String|Array} options.field - This parameter allows you to specify a field which should be treated as a facet. It will iterate over each Term in the field and generate a facet count using that Term as the constraint. Multiple fields can be defined providing an array instead of a string.
   * @param {String} [options.prefix] - Limits the terms on which to facet to those starting with the given string prefix.
   * @param {String} [options.sort] - This param determines the ordering of the facet field constraints.count
   * @param {Number} [options.limit=100] - This parameter indicates the maximum number of constraint counts that should be returned for the facet fields. A negative value means unlimited.The solr's default value is 100.
   * @param {Number} [options.offset=0] - This param indicates an offset into the list of constraints to allow paging.The solr's default value is 0.
   * @param {Number} [options.mincount=0] - This parameter indicates the minimum counts for facet fields should be included in the response. The solr's default value is 0.
   * @param {Boolean} [options.missing=false] - Set to `true` this param indicates that in addition to the Term based constraints of a facet field, a count of all matching results which have no value for the field should be computed. The solr's default value is false.
   * @param {String} [options.method="fc"] - This parameter indicates what type of algorithm/method to use when faceting a field.The solr's default value is fc (except for BoolField).
   * @param {String|Array} options.pivot - This parameter allows you to specify a field which should be treated as a facet pivot. It will iterate over each Term in the field. Multiple fields can be defined providing an array instead of a string.
   * @param {String} [options.pivot.mincount=0] - This parameter indicates the minimum counts for facet pivot fields to be included in the response. The solr's default value is 0.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  facet(options: FacetOptions): SolrQueryBuilder {
    const self = this
    if (options.on === false) {
      this.parameters.push("facet=false")
    } else {
      this.parameters.push("facet=true")
    }
    if (options.query) {
      this.parameters.push("facet.query=" + encodeURIComponent(options.query))
    }
    if (options.field) {
      options.field = arrayUtils.toArray(options.field)
      options.field.forEach(function (field) {
        self.parameters.push("facet.field=" + field)
      })
    }
    if (options.prefix) {
      this.parameters.push("facet.prefix=" + encodeURIComponent(options.prefix))
    }
    if (options.sort) {
      this.parameters.push("facet.sort=" + encodeURIComponent(options.sort))
    }
    if (options.limit !== undefined) {
      this.parameters.push("facet.limit=" + options.limit)
    }
    if (options.offset !== undefined) {
      this.parameters.push("facet.offset=" + options.offset)
    }
    if (options.mincount !== undefined) {
      this.parameters.push("facet.mincount=" + options.mincount)
    }
    if (options.missing !== undefined) {
      this.parameters.push("facet.missing=" + options.missing)
    }
    if (options.method) {
      this.parameters.push("facet.method=" + options.method)
    }

    // Only supported with version 4.0 and above
    if (this.solrVersion && versionUtils.version(this.solrVersion) >= versionUtils.Solr4_0) {
      if (options.pivot) {
        options.field = arrayUtils.toArray(options.pivot.fields)
        options.field.forEach(function (field) {
          self.parameters.push("facet.pivot=" + field)
        })

        if (options.pivot.mincount) {
          this.parameters.push("facet.pivot.mincount=" + options.pivot.mincount)
        }
      }
    }

    return self
  }

  /**
   * Create a MoreLikeThis. MoreLikeThis constructs a lucene query based on terms within a document.
   *
   * @param {Object} options - set of options to create a morelikethis
   * @param {Boolean} [options.on=true] - Turn on or off morelikethis
   * @param {String|Array} [options.fl] - The fields to use for similarity. NOTE: if possible, these should have a stored TermVector
   * @param {Number} [options.count] - The number of similar documents to return for each result.
   * @param {Number} [options.mintf] - Minimum Term Frequency - the frequency below which terms will be ignored in the source doc.
   * @param {Number} [options.mindf] - Minimum Document Frequency - the frequency at which words will be ignored which do not occur in at least this many docs.
   * @param {Number} [options.minwl] - minimum word length below which words will be ignored.
   * @param {Number} [options.maxwl] - maximum word length above which words will be ignored.
   * @param {Number} [options.maxqt] - maximum number of query terms that will be included in any generated query.
   * @param {Number} [options.maxntp] - maximum number of tokens to parse in each example doc field that is not stored with TermVector support.
   * @param {Boolean} [options.boost] - set if the query will be boosted by the interesting term relevance.
   * @param {String|Object} [options.qf] - Query fields and their boosts using the same format as that used in DisMaxQParserPlugin. These fields must also be specified in mlt.fl.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  mlt(options: MltOptions): SolrQueryBuilder {
    let parameter
    const self = this
    if (options.on === false) {
      this.parameters.push("mlt=false")
    } else {
      this.parameters.push("mlt=true")
    }
    if (options.fl) {
      if (options.fl instanceof Array) options.fl = options.fl.join(",")
      this.parameters.push("mlt.fl=" + encodeURIComponent(options.fl))
    }
    if (options.count !== undefined) {
      this.parameters.push("mlt.count=" + options.count)
    }
    if (options.mintf !== undefined) {
      this.parameters.push("mlt.mintf=" + options.mintf)
    }
    if (options.mindf !== undefined) {
      this.parameters.push("mlt.mindf=" + options.mindf)
    }
    if (options.minwl !== undefined) {
      this.parameters.push("mlt.minwl=" + options.minwl)
    }
    if (options.maxwl !== undefined) {
      this.parameters.push("mlt.maxwl=" + options.maxwl)
    }
    if (options.maxqt !== undefined) {
      this.parameters.push("mlt.maxqt=" + options.maxqt)
    }
    if (options.maxntp !== undefined) {
      this.parameters.push("mlt.maxntp=" + options.maxntp)
    }
    if (options.boost !== undefined) {
      this.parameters.push("mlt.boost=" + options.boost)
    }
    if (options.qf) {
      if (typeof options.qf === "object") {
        parameter = queryStringify(options.qf, "%20", "^", {}, this.isGlobalIndex)
      } else {
        parameter = encodeURIComponent(options.qf)
      }
      this.parameters.push("mlt.qf=" + parameter)
    }
    return self
  }

  /*!
   * DisMax parameters
   * do not forget to use `.dismax()` when using these parameters
   */

  /**
   * Use the DisMax query parser
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  dismax(): SolrQueryBuilder {
    const self = this
    this.defType("dismax")
    return self
  }

  /*!
   * EDisMax parameters
   * do not forget to use `.edismax()` when using these parameters
   */

  /**
   * Use the EDisMax query parser
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  edismax(): SolrQueryBuilder {
    const self = this
    this.defType("edismax")
    return self
  }

  /**
   * Add the parameter debugQuery.
   * Additional debugging informations will be available in the response.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  debugQuery(): SolrQueryBuilder {
    const self = this
    this.parameters.push("debugQuery=true")
    return self
  }

  /**
   * Set the "boosts" to associate with each fields
   *
   * @param {Object} options -
   *
   * @return {SolrQueryBuilder}
   * @api public
   *
   * @example
   * const query = client.query();
   * query.qf({title : 2.2, description : 0.5 });
   */

  qf(options: Record<string, any>): SolrQueryBuilder {
    const self = this
    let parameter = "qf="
    parameter += queryStringify(options, "%20", "^", {}, this.isGlobalIndex)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the minimum number or percent of clauses that must match.
   *
   * @param {String|Number} minimum - number or percent of clauses that must match
   *
   * @return {SolrQueryBuilder}
   * @api public
   *
   * @example
   * var query = client.query();
   * query.mm(2); // or query.mm('75%');
   */

  mm(minimum: string | number): SolrQueryBuilder {
    const self = this
    const parameter = "mm=" + minimum
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the Phrase Fields parameter.
   * Once the list of matching documents has been identified using the "fq" and "qf" params, the "pf" param can be used to "boost" the score of documents in cases where all of the terms
   * in the "q" param appear in close proximity.
   *
   * @param {Object} options -
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  pf(options: Record<string, any>): SolrQueryBuilder {
    const self = this
    let parameter = "pf="
    parameter += queryStringify(options, "%20", "^", {}, this.isGlobalIndex)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the phrase slop allowed in a query.
   *
   * @param {Number} slop - Amount of phrase slop allowed by the query filter. This value should represent the maximum number of words allowed between words in a field that match a phrase in the query.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  ps(slop: number): SolrQueryBuilder {
    const self = this
    const parameter = "ps=" + slop
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the query slop allowed in a query.
   *
   * @param {Number} slop - Amount of query slop allowed by the query filter. This value should be used to affect boosting of query strings.
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  qs(slop: number): SolrQueryBuilder {
    const self = this
    const parameter = "qs=" + slop
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the tiebreaker in DisjunctionMaxQueries (should be something much less than 1)
   *
   * @param {Float|Number} tiebreaker -
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  tie(tiebreaker: number): SolrQueryBuilder {
    const self = this
    const parameter = "tie=" + tiebreaker
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the Boost Query parameter.
   * A raw query string (in the SolrQuerySyntax) that will be included with the user's query to influence the score. If this is a BooleanQuery with a default boost (1.0f) then the individual clauses will be added directly to the main query. Otherwise, the query will be included as is.
   *
   * @param {Object} options -
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  bq(options: Record<string, any>): SolrQueryBuilder {
    const self = this
    let parameter = "bq="
    parameter += queryStringify(options, "%20", ":", {}, this.isGlobalIndex)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the Functions (with optional boosts) that will be included in the user's query to influence the score.
   * @param {String} functions - e.g.: `recip(rord(myfield),1,2,3)^1.5`
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  bf(functions: string): SolrQueryBuilder {
    const self = this
    const parameter = "bf=" + functions
    this.parameters.push(parameter)
    return self
  }

  /**
   * Set the Functions (with optional boosts) that will be included in the user's query to influence the score.
   * @param {String} functions - e.g.: `recip(rord(myfield),1,2,3)^1.5`
   *
   * @return {SolrQueryBuilder}
   * @api public
   */

  boost(functions: string): SolrQueryBuilder {
    const self = this
    const parameter = "boost=" + encodeURIComponent(functions)
    this.parameters.push(parameter)
    return self
  }

  /**
   * Build a querystring with the array of `this.parameters`.
   *
   * @return {String}
   * @api private
   */
  build(): string {
    return this.parameters.join("&")
  }

  /**
   * Set the Query Highlighting parameter.
   *
   * @param {Object} options - set of options for Highlighting
   * @param {Boolean} [options.on=true] - Turn on or off Highlighting
   * @param {String|Array} [options.q] - This parameters specifies and overriding query for highlighting. Multiple values specified in an array will be chained together with AND.
   * @param {String} [options.qparser] - This parameter specifies the qparser for the hl.q query.
   * @param {String|Array} [options.fl] - 'Field list.' Fields to be highlighted. Multiple fields can be entered by providing an array.
   * @param {Number} [options.snippets] - This parameter defines the maximum number of snippets to generate per field. Any number of snippets from 0 to this number can be generated per field
   * @param {Number} [options.fragsize] - This parameter defines the size, in characters, of the fragments to consider for highlighting.
   * @param {Boolean} [options.mergeContiguous] - This parameter instructs Solr to collapse continguous fragments into a single fragment.
   * @param {Number} [options.maxAnalyzedChars] - This param specifies the number of characters into a document that Solr should look for suitable snippets.
   * @param {Number} [options.maxMultiValuedToExamine] - This param specifies the max number of entries in a multi-valued field to examine before stopping
   * @param {Number} [options.maxMultiValuedToMatch] - This param specifies the maximum number of matches in a multi-valued field that are found before stopping.
   * @param {String} [options.alternateField] - Specifies a field to be used as a backup default summary if Solr cannot generate a snippet.
   * @param {Number} [options.maxAlternateFieldLength] - Specifies the maximum number of characters of the field to return. A number <=0 means the field length is unlimited.
   * @param {String} [options.formatter] - Selects a formatter for the highlighted output. At the time of writing, the only legal value is 'simple'.
   * @param {String} [options.simplePre] - This parameter defines the string to place before the data to be highlighted.
   * @param {String} [options.simplePost] - This parameter defines the string to place after the data to be highlighted.
   * @param {String} [options.fragmenter] - Specifies a text snippet generator for highlighted text. Default is 'gap' but 'regex' is another option.
   * @param {Boolean} [options.highlightMultiTerm] - Turn on or off MultiTermHighlighting. If True, Solr will use Highlight phrase terms that appear in multiple fields.
   * @param {Boolean} [options.requireFieldMatch] - If set to True, this parameter will force Solr to highlight terms only if they appear in the specified field. If false, terms are highlighted in all requested fields regardless of which field matches the query.
   * @param {Boolean} [options.usePhraseHighlighter] - If set to True, Solr will use the Lucene SpanScorer class to highlight phrase terms only when they appear within the query phrase in the document.
   * @param {Number} [options.regexSlop] - When using the regex fragmenter, this number specifies the factor by which the fragmenter can stray from the ideal fragment size.
   * @param {String} [options.regexPattern] - This parameter specifies the regulat expression for fragmenting.
   * @param {Number} [options.regexMaxAnalyzedChars] - This parameters specifies the max number of characters to analyze from a field when using the regex fragmenter.
   * @param {Boolean} [options.preserveMulti] - If True, multi-valued fields will return all values in the order they were saved in the index. If False, only values that match the highlight request will be returned.
   * @param {Boolean} [options.payloads] - If usePhraseHighlighter is True, and the indexed field has payloads but not term vectors, the index payloads will be read into the highlighter's index along with the posting. If you don't want this behavior, you may set this parameter to False and save some memory.

   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  hl(options: HlOptions): SolrQueryBuilder {
    const self = this
    if (options.on === false) {
      this.parameters.push("hl=false")
    } else {
      this.parameters.push("hl=true")
    }
    if (options.q !== undefined) {
      if (typeof options.q === "string") {
        this.parameters.push("hl.q=" + encodeURIComponent(options.q))
      } else {
        this.parameters.push(
          "hl.q=" + queryStringify(options.q, "%20AND%20", ":", {}, this.isGlobalIndex)
        )
      }
    }
    if (options.qparser !== undefined) {
      this.parameters.push("hl.qparser=" + encodeURIComponent(options.qparser))
    }
    if (options.fl !== undefined) {
      if (typeof options.fl === "string") {
        this.parameters.push("hl.fl=" + encodeURIComponent(options.fl))
      } else {
        this.parameters.push("hl.fl=" + options.fl.join(","))
      }
    }
    if (options.snippets !== undefined) {
      this.parameters.push("hl.snippets=" + encodeURIComponent(options.snippets))
    }
    if (options.fragsize !== undefined) {
      this.parameters.push("hl.fragsize=" + encodeURIComponent(options.fragsize))
    }
    if (options.mergeContiguous !== undefined) {
      this.parameters.push("hl.mergeContiguous=" + encodeURIComponent(options.mergeContiguous))
    }
    if (options.requireFieldMatch !== undefined) {
      this.parameters.push("hl.requireFieldMatch=" + encodeURIComponent(options.requireFieldMatch))
    }
    if (options.maxAnalyzedChars !== undefined) {
      this.parameters.push("hl.maxAnalyzedChars=" + encodeURIComponent(options.maxAnalyzedChars))
    }
    if (options.maxMultiValuedToExamine !== undefined) {
      this.parameters.push(
        "hl.maxMultiValuedToExamine=" + encodeURIComponent(options.maxMultiValuedToExamine)
      )
    }
    if (options.maxMultiValuedToMatch !== undefined) {
      this.parameters.push(
        "hl.maxMultiValuedToMatch=" + encodeURIComponent(options.maxMultiValuedToMatch)
      )
    }
    if (options.alternateField) {
      this.parameters.push("hl.alternateField=" + encodeURIComponent(options.alternateField))
    }
    if (options.maxAlternateFieldLength !== undefined) {
      this.parameters.push(
        "hl.maxAlternateFieldLength=" + encodeURIComponent(options.maxAlternateFieldLength)
      )
    }
    if (options.formatter) {
      this.parameters.push("hl.formatter=" + encodeURIComponent(options.formatter))
    }
    if (options.simplePre) {
      this.parameters.push("hl.simple.pre=" + encodeURIComponent(options.simplePre))
    } else {
      this.parameters.push("hl.simple.pre=<em>")
    }
    if (options.simplePost) {
      this.parameters.push("hl.simple.post=" + encodeURIComponent(options.simplePost))
    } else {
      this.parameters.push("hl.simple.post=<%2Fem>")
    }
    if (options.fragmenter) {
      this.parameters.push("hl.fragmenter=" + encodeURIComponent(options.fragmenter))
    }
    if (options.highlightMultiTerm !== undefined) {
      this.parameters.push(
        "hl.highlightMultiTerm=" + encodeURIComponent(options.highlightMultiTerm)
      )
    }
    if (options.usePhraseHighlighter !== undefined) {
      this.parameters.push(
        "hl.usePhraseHighlighter=" + encodeURIComponent(options.usePhraseHighlighter)
      )
    }
    if (options.regexSlop !== undefined) {
      this.parameters.push("hl.regex.slop=" + encodeURIComponent(options.regexSlop))
    }
    if (options.regexPattern) {
      this.parameters.push("hl.regex.pattern=" + encodeURIComponent(options.regexPattern))
    }
    if (options.regexMaxAnalyzedChars) {
      this.parameters.push(
        "hl.regex.maxAnalyzedChars=" + encodeURIComponent(options.regexMaxAnalyzedChars)
      )
    }
    if (options.preserveMulti !== undefined) {
      this.parameters.push("hl.preserveMulti=" + encodeURIComponent(options.preserveMulti))
    }
    if (options.payloads !== undefined) {
      this.parameters.push("hl.payloads=" + encodeURIComponent(options.payloads))
    }

    return self
  }

  /**
   * Create a terms
   *
   * @param {Object} options - set of options to create a terms
   * @param {Boolean} [options.on=true] - Turn on or off terms
   * @param {String} options.fl - The name of the field to get the terms from.
   * @param {String} [options.lower] - The term to start at. If not specified, the empty string is used, meaning start at the beginning of the field.
   * @param {Boolean} [options.lowerIncl] - The term to start at. Include the lower bound term in the result set. Default is true.
   * @param {Number} [options.mincount] - The minimum doc frequency to return in order to be included.
   * @param {Number} [options.maxcount] - The maximum doc frequency.
   * @param {String} [options.prefix] - Restrict matches to terms that start with the prefix.
   * @param {String} [options.regex] - Restrict matches to terms that match the regular expression.
   * @param {String} [options.regexFlag] - Flags to be used when evaluating the regular expression defined in the "terms.regex" parameter.(case_insensitive|comments|multiline|literal|dotall|unicode_case|canon_eq|unix_lines)
   * @param {Number} [options.limit] - The maximum number of terms to return.
   * @param {String} [options.upper] - The term to stop at. Either upper or terms.limit must be set.
   * @param {Boolean} [options.upperIncl] - Include the upper bound term in the result set. Default is false.
   * @param {Boolean} [options.raw] - If true, return the raw characters of the indexed term, regardless of if it is human readable.
   * @param {String} [options.sort] - If count, sorts the terms by the term frequency (highest count first). If index, returns the terms in index order.(count|index)
   *
   * @return {SolrQueryBuilder}
   * @api public
   */
  terms(options: TermsOptions): SolrQueryBuilder {
    const self = this
    if (options.on === false) {
      this.parameters.push("terms=false")
    } else {
      this.parameters.push("terms=true")
    }
    if (options.fl) {
      this.parameters.push("terms.fl=" + encodeURIComponent(options.fl))
    }
    if (options.lower !== undefined) {
      this.parameters.push("terms.lower=" + encodeURIComponent(options.lower))
    }
    if (options.lowerIncl !== undefined) {
      this.parameters.push("terms.lower.incl=" + encodeURIComponent(options.lowerIncl))
    }
    if (options.mincount !== undefined) {
      this.parameters.push("terms.mincount=" + options.mincount)
    }
    if (options.maxcount !== undefined) {
      this.parameters.push("terms.maxcount=" + options.maxcount)
    }
    if (options.prefix !== undefined) {
      this.parameters.push("terms.prefix=" + encodeURIComponent(options.prefix))
    }
    if (options.regex !== undefined) {
      this.parameters.push("terms.regex=" + encodeURIComponent(options.regex))
    }
    if (options.regexFlag !== undefined) {
      this.parameters.push("terms.regexFlag=" + encodeURIComponent(options.regexFlag))
    }
    if (options.limit !== undefined) {
      this.parameters.push("terms.limit=" + options.limit)
    }
    if (options.upper !== undefined) {
      this.parameters.push("terms.upper=" + encodeURIComponent(options.upper))
    }
    if (options.upperIncl !== undefined) {
      this.parameters.push("terms.upper.incl=" + encodeURIComponent(options.upperIncl))
    }
    if (options.raw !== undefined) {
      this.parameters.push("terms.raw=" + encodeURIComponent(options.raw))
    }
    if (options.sort !== undefined) {
      this.parameters.push("terms.sort=" + encodeURIComponent(options.sort))
    }
    return self
  }
}
